'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface SectorProgram {
  name: string;
  components: string[];
  approvals?: string[];
}

interface Sector {
  id: string;
  icon: string;
  label: string;
  title: string;
  description: string;
  programs: SectorProgram[];
  materials: string[];
  accentColor: string;
  borderColor: string;
  badgeColor: string;
  videoSlot: string;
  videoUrl?: string;
}

const sectors: Sector[] = [
  {
    id: 'commercial',
    icon: 'PaperAirplaneIcon',
    label: 'Commercial Aviation',
    title: 'Commercial Aircraft',
    description:
      'Approved supplier delivering precision cabin crew structural sheet metal components and structural assemblies for major commercial aviation programs.',
    programs: [
      {
        name: 'Airbus A320 Program',
        components: ['Cabin Crew Structural Components', 'Sheet Metal Fabrications', 'Structural Brackets & Panels'],
        approvals: ['Airbus Approved Supplier'],
      },
      {
        name: 'Rafale Program',
        components: ['Class 2 & Class 3 Structural Parts', 'Precision Machined Components'],
        approvals: ['Rafale Approved – Class 2 & 3'],
      },
    ],
    materials: ['Al 6061', 'Al 7075', 'SS 304/316', 'Titanium', 'Brass'],
    accentColor: 'text-accent',
    borderColor: 'border-accent/30',
    badgeColor: 'bg-accent/10 border-accent/30 text-accent',
    videoSlot: 'commercial',
    videoUrl: 'https://res.cloudinary.com/hukx0umi/video/upload/v1783350888/Welcome_on_board_airbus_helicopters-2_vdzorn.mp4',
  },
  {
    id: 'defence',
    icon: 'ShieldCheckIcon',
    label: 'Defence Systems',
    title: 'Defence',
    description:
      'Mission-critical structural, fuselage, and system components for India\'s frontline defence platforms — from fighter jets to unmanned aerial vehicles.',
    programs: [
      {
        name: 'LCA Tejas',
        components: ['Structural Components', 'Window Frames', 'Wing Components', 'Fuselage Assemblies'],
      },
      {
        name: 'Sukhoi Su-30 MKI',
        components: ['Structural Components', 'Fuselage Parts', 'Precision Machined Assemblies'],
      },
      {
        name: 'UAV Programs',
        components: ['Engine Complete Assembly', 'Structural Components for Remote Weapon Systems'],
      },
      {
        name: 'Reverse Engineering',
        components: ['Legacy Component Reverse Engineering', 'Drawing Generation from Physical Parts'],
      },
    ],
    materials: ['Al 7075', 'Al 6061', 'Titanium', 'SS 17-4PH', 'Inconel'],
    accentColor: 'text-mission-orange',
    borderColor: 'border-mission-orange/30',
    badgeColor: 'bg-mission-orange/10 border-mission-orange/30 text-mission-orange',
    videoSlot: 'defence',
    videoUrl: 'https://res.cloudinary.com/hukx0umi/video/upload/v1783350946/UAVs_jaurj3.mp4',
  },
  {
    id: 'space',
    icon: 'RocketLaunchIcon',
    label: 'Space Systems',
    title: 'Space',
    description:
      'Precision-engineered structural and support systems for satellite platforms and launch vehicles, built to withstand the extreme demands of space environments.',
    programs: [
      {
        name: 'Satellite Systems',
        components: ['Satellite Structural Frames', 'Payload Support Structures', 'Thermal Management Brackets'],
      },
      {
        name: 'Launch Vehicles',
        components: ['Launcher Structural Components', 'Stage Separation Hardware', 'Fairing Structures'],
      },
      {
        name: 'Structural Support Systems',
        components: ['Ground Support Equipment', 'Integration Fixtures', 'Precision Alignment Structures'],
      },
    ],
    materials: ['Al 2024', 'Al 7075', 'Titanium Ti-6Al-4V', 'Invar', 'CFRP Inserts'],
    accentColor: 'text-aerospace-green',
    borderColor: 'border-aerospace-green/30',
    badgeColor: 'bg-aerospace-green/10 border-aerospace-green/30 text-aerospace-green',
    videoSlot: 'space',
    videoUrl: 'https://res.cloudinary.com/hukx0umi/video/upload/v1783313253/From_Klickpin.com-_From_beginner_to_obsessed_Steal_these_budget-friendly_cat_enrichment_ideas_that_feel_luxe_without_spending_a_fortune_with_smart_d7rjym.mp4',
  },
  {
    id: 'robotics',
    icon: 'CpuChipIcon',
    label: 'Robotics & Automation',
    title: 'Robotic Arms & Automation',
    description:
      'High-precision structural parts for robotic arm systems and industrial automation platforms, engineered for repeatability and load-bearing performance.',
    programs: [
      {
        name: 'Robotic Arm Structures',
        components: ['Arm Link Structural Parts', 'Joint Housing & Brackets', 'End-Effector Mounts'],
      },
      {
        name: 'Automation Systems',
        components: ['Frame & Chassis Components', 'Precision Mounting Plates', 'Custom Structural Assemblies'],
      },
    ],
    materials: ['Al 6061', 'Al 7075', 'SS 304', 'Brass', 'Titanium'],
    accentColor: 'text-accent',
    borderColor: 'border-accent/30',
    badgeColor: 'bg-accent/10 border-accent/30 text-accent',
    videoSlot: 'robotics',
    videoUrl: 'https://res.cloudinary.com/hukx0umi/video/upload/v1783351083/From_Klickpin.com-_19_Fresh_side_hustle_ideas_that_look_expensive_while_staying_practical_realistic_and_beginner_friendly_for_people_who_want_styl_ujsf85.mp4',
  },
];

const AerospaceSectors = () => {
  const [activeSector, setActiveSector] = useState<string>('commercial');

  const active = sectors.find((s) => s.id === activeSector) ?? sectors[0];

  return (
    <section className="relative py-24 bg-gradient-to-b from-primary to-background overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-accent rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-mission-orange rounded-full blur-3xl" />
      </div>

      <div className="relative mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/30 mb-6">
            <Icon name="GlobeAltIcon" size={20} className="text-accent" />
            <span className="font-cta text-sm text-accent">Sectors We Serve</span>
          </div>
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Aerospace &amp; Defence Expertise
          </h2>
          <p className="font-body text-lg text-technical-gray">
            From commercial airliners to deep-space launchers — Wings Bharath delivers certified, mission-ready components across every critical sector.
          </p>
        </div>

        {/* Sector Tab Switcher */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {sectors.map((sector) => (
            <button
              key={sector.id}
              onClick={() => setActiveSector(sector.id)}
              className={`flex items-center space-x-2 px-5 py-3 rounded-full border font-cta text-sm transition-all duration-300 ${
                activeSector === sector.id
                  ? `bg-accent text-primary border-accent shadow-glow`
                  : 'bg-card/50 border-border text-technical-gray hover:border-accent/50 hover:text-accent'
              }`}
            >
              <Icon name={sector.icon as any} size={18} />
              <span>{sector.label}</span>
            </button>
          ))}
        </div>

        {/* Active Sector Detail */}
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Left: Info */}
          <div className="space-y-8">
            {/* Title & Description */}
            <div>
              <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full border mb-4 ${active.badgeColor}`}>
                <Icon name={active.icon as any} size={16} />
                <span className="font-cta text-xs uppercase tracking-wider">{active.label}</span>
              </div>
              <h3 className="font-headline text-3xl lg:text-4xl font-bold text-foreground mb-4">
                {active.title}
              </h3>
              <p className="font-body text-lg text-technical-gray leading-relaxed">
                {active.description}
              </p>
            </div>

            {/* Programs */}
            <div className="space-y-4">
              <p className={`font-cta text-xs uppercase tracking-widest ${active.accentColor}`}>
                Programs &amp; Components
              </p>
              {active.programs.map((prog) => (
                <div
                  key={prog.name}
                  className={`bg-card/60 backdrop-blur-sm border rounded-xl p-5 ${active.borderColor}`}
                >
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <h4 className="font-headline text-base font-bold text-foreground">{prog.name}</h4>
                    {prog.approvals && (
                      <div className="flex flex-wrap gap-1 shrink-0">
                        {prog.approvals.map((a) => (
                          <span
                            key={a}
                            className="px-2 py-0.5 bg-aerospace-green/20 border border-aerospace-green/30 rounded-md font-cta text-[10px] text-aerospace-green whitespace-nowrap"
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ul className="space-y-1.5">
                    {prog.components.map((comp) => (
                      <li key={comp} className="flex items-center space-x-2">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active.accentColor.replace('text-', 'bg-')}`} />
                        <span className="font-body text-sm text-technical-gray">{comp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Materials */}
            <div>
              <p className={`font-cta text-xs uppercase tracking-widest mb-3 ${active.accentColor}`}>
                Primary Materials
              </p>
              <div className="flex flex-wrap gap-2">
                {active.materials.map((mat) => (
                  <span
                    key={mat}
                    className="px-3 py-1.5 bg-muted border border-border rounded-lg font-body text-sm text-technical-gray"
                  >
                    {mat}
                  </span>
                ))}
              </div>
            </div>

            <Link
              href="/capabilities"
              className="inline-flex items-center space-x-2 text-accent hover:text-mission-orange transition-colors duration-300"
            >
              <span className="font-cta text-sm">Explore Capabilities</span>
              <Icon name="ArrowRightIcon" size={16} />
            </Link>
          </div>

          {/* Right: Video Slot */}
          <div className="relative bg-card/30 backdrop-blur-sm border border-border rounded-2xl overflow-hidden shadow-aerospace">
            <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
              {/* Video element */}
              <video
                key={active.videoSlot}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                style={{ background: 'linear-gradient(135deg, #050a14 0%, #0a1628 50%, #0d1f3c 100%)' }}
              >
                {active.videoUrl && <source src={active.videoUrl} type="video/mp4" />}
              </video>

              {/* Placeholder overlay — hidden when video is available */}
              {!active.videoUrl && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #050a14 0%, #0a1628 60%, #0d1f3c 100%)' }}
                >
                  <div className={`w-20 h-20 rounded-full border-2 flex items-center justify-center mb-4 ${active.borderColor}`}>
                    <Icon name={active.icon as any} size={36} className={active.accentColor} />
                  </div>
                  <p className="font-cta text-xs text-technical-gray tracking-widest text-center px-6">
                    VIDEO COMING SOON
                  </p>
                  <p className="font-body text-xs text-technical-gray/50 mt-1 text-center px-6">
                    {active.title} showcase
                  </p>
                </div>
              )}

              {/* HUD badge */}
              <div className={`absolute top-3 left-3 flex items-center space-x-1.5 px-2 py-1 bg-black/50 backdrop-blur-sm rounded border ${active.borderColor}`}>
                <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${active.accentColor.replace('text-', 'bg-')}`} />
                <span className={`font-cta text-[10px] tracking-widest ${active.accentColor}`}>
                  {active.label.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="px-6 py-4 border-t border-border/50 flex items-center justify-between">
              <span className="font-body text-xs text-technical-gray">
                {active.programs.length} program{active.programs.length > 1 ? 's' : ''} · {active.materials.length} material grades
              </span>
              <div className="flex items-center space-x-1">
                <span className="w-1 h-1 rounded-full bg-aerospace-green animate-pulse" />
                <span className="font-cta text-[10px] text-aerospace-green">CERTIFIED</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats Strip */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Commercial Programs', value: '200+', icon: 'PaperAirplaneIcon' },
            { label: 'Defence Platforms', value: '30+', icon: 'ShieldCheckIcon' },
            { label: 'Space Programs', value: '10+', icon: 'RocketLaunchIcon' },
            { label: 'Material Grades', value: '20+', icon: 'CubeIcon' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-card/50 backdrop-blur-sm border border-border rounded-xl p-5 text-center hover:border-accent transition-all duration-300 hover:shadow-glow"
            >
              <Icon name={stat.icon as any} size={28} className="text-accent mx-auto mb-2" />
              <p className="font-headline text-3xl font-bold text-foreground">{stat.value}</p>
              <p className="font-body text-xs text-technical-gray mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AerospaceSectors;
